/**
 * Lightweight coordinator so GenieMascot and MagicPath stay in sync
 * without React state updates on scroll frames.
 */
class GenieTransitionBus {
    constructor() {
        this.currentSectionId = "#hero";
        this.isTransitioning = false;
        this.pendingSectionId = null;
        this.pathHandler = null;
        this.genieHandler = null;
        this.activeRunId = 0;
        this.flushPromise = null;
    }

    registerPath(handler) {
        this.pathHandler = handler;
        return () => {
            if (this.pathHandler === handler) this.pathHandler = null;
        };
    }

    registerGenie(handler) {
        this.genieHandler = handler;
        return () => {
            if (this.genieHandler === handler) this.genieHandler = null;
        };
    }

    killActive() {
        this.pathHandler?.kill?.();
        this.genieHandler?.kill?.();
    }

    abortActiveRun() {
        this.activeRunId += 1;
        this.killActive();
        this.isTransitioning = false;
    }

    request(sectionId) {
        if (sectionId === this.currentSectionId && !this.isTransitioning) return;
        this.pendingSectionId = sectionId;
        if (this.isTransitioning) {
            this.abortActiveRun();
        }
        return this.flush();
    }

    consumePending(fallback) {
        const next = this.pendingSectionId || fallback;
        this.pendingSectionId = null;
        return next;
    }

    flush() {
        if (this.flushPromise) return this.flushPromise;
        this.flushPromise = this._flushLoop().finally(() => {
            this.flushPromise = null;
        });
        return this.flushPromise;
    }

    async _flushLoop() {
        while (this.pendingSectionId && this.pendingSectionId !== this.currentSectionId) {
            const fromId = this.currentSectionId;
            const toId = this.pendingSectionId;
            this.pendingSectionId = null;
            await this.run(fromId, toId);
        }
    }

    run(fromId, toId) {
        const runId = (this.activeRunId += 1);

        return new Promise((resolve) => {
            this.isTransitioning = true;
            this.killActive();

            const isStale = () => runId !== this.activeRunId;

            const finish = (resolvedId) => {
                if (isStale()) {
                    resolve();
                    return;
                }
                this.currentSectionId = resolvedId;
                this.isTransitioning = false;
                resolve();
                if (this.pendingSectionId && this.pendingSectionId !== this.currentSectionId) {
                    this.flush();
                }
            };

            const runSequence = async () => {
                await this.genieHandler?.hide?.();
                if (isStale()) {
                    resolve();
                    return;
                }

                let targetId = this.consumePending(toId);

                if (targetId === "#hero") {
                    await this.genieHandler?.restoreHero?.(targetId);
                } else {
                    await this.pathHandler?.play?.(fromId, targetId);
                    if (isStale()) {
                        resolve();
                        return;
                    }

                    targetId = this.consumePending(targetId);
                    if (targetId === "#hero") {
                        await this.genieHandler?.restoreHero?.(targetId);
                    } else {
                        await this.genieHandler?.reveal?.(targetId);
                    }
                }
                if (isStale()) {
                    resolve();
                    return;
                }

                this.genieHandler?.enforcePose?.(targetId);
                finish(targetId);
            };

            runSequence();
        });
    }

    settle(sectionId, instant = false) {
        this.abortActiveRun();
        this.currentSectionId = sectionId;
        this.pendingSectionId = null;
        this.genieHandler?.settle?.(sectionId, instant);
        if (instant) {
            this.genieHandler?.enforcePose?.(sectionId);
        }
    }
}

export const genieTransitionBus = new GenieTransitionBus();
